/**
 * DualConsentBanner — top-of-dashboard prompt that surfaces while the
 * teen-parent Charter is still in `pending_parent` or `pending_teen`.
 *
 * Calls /api/family/consent-banner on mount; renders nothing
 * unless the server says the user needs to take action. Two states:
 *   • teen w/ pending_parent  → "Your parent hasn't signed yet" (info only)
 *   • teen w/ pending_teen    → CTA → /charter (sign + activate)
 *   • parent w/ pending_parent → CTA → /charter (sign first)
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScrollText, AlertCircle } from 'lucide-react';
import { apiClient } from '../../api/client';

const DualConsentBanner = () => {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    apiClient
      .request('/api/family/consent-banner')
      .then((r) => setData(r))
      .catch(() => setData(null));
  }, []);

  if (!data || !data.show_banner) return null;

  const isPendingParent = data.status === 'pending_parent';
  const cta = isPendingParent && data.role === 'teen' ? null : 'Open Charter';

  return (
    <div
      className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 mb-4 flex items-start gap-3"
      data-testid="dual-consent-banner"
    >
      <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
        {isPendingParent && data.role === 'teen' ? (
          <AlertCircle size={18} className="text-amber-700" />
        ) : (
          <ScrollText size={18} className="text-amber-700" />
        )}
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold text-amber-900" data-testid="dual-consent-banner-title">
          {data.role === 'teen' ? 'Charter co-signature' : 'Charter signature needed'}
        </p>
        <p className="text-xs text-amber-800 mt-0.5" data-testid="dual-consent-banner-message">
          {data.message}
        </p>
      </div>
      {cta && (
        <button
          onClick={() => navigate('/charter')}
          className="text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 px-3 py-1.5 rounded-lg shrink-0"
          data-testid="dual-consent-banner-cta"
        >
          {cta}
        </button>
      )}
    </div>
  );
};

export default DualConsentBanner;
